<template>
  <div class="it-user-list-view pa-4">
    <!-- Khung Card chính chứa danh sách tài khoản IT -->
    <v-card class="rounded-xl elevation-3" flat>
      <!-- Header tiêu đề trang và các nút thao tác chung -->
      <div class="d-flex align-center justify-space-between pt-4 pb-2 px-6">
        <div>
          <h1 class="text-h6 font-weight-bold teal--text text--darken-4 mb-1">
            Danh sách tài khoản
          </h1>
          <div class="text-caption grey--text text--darken-1">
            Quản trị tài khoản, thiết bị đăng nhập, gương mặt và cấu hình chấm công
          </div>
        </div>

        <div class="d-flex align-center">
          <!-- Nút Làm mới chuẩn giao diện (viền xanh nhạt, icon xanh đậm) -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                outlined
                color="green lighten-3"
                class="rounded-lg mr-2"
                height="40"
                width="40"
                v-bind="attrs"
                v-on="on"
                @click="loadUsers"
                :loading="isLoading"
              >
                <v-icon color="green darken-4">mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Tải lại danh sách</span>
          </v-tooltip>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Khung bộ lọc tìm kiếm chuẩn hệ thống (Filter Bar) -->
      <v-sheet class="grey lighten-4 py-6">
        <v-container fluid class="py-0 px-6">
          <v-row dense align="center">
            <!-- Ô tìm kiếm từ khóa chung -->
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="search"
                label="Tìm kiếm nhanh..."
                placeholder="Mã NS, tên NS, tài khoản, phòng ban..."
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                clearable
                hide-details
                background-color="white"
              ></v-text-field>
            </v-col>

            <!-- Lọc theo Khối (Department Type) -->
            <v-col cols="12" sm="6" md="2">
              <v-autocomplete
                v-model="filterDeptType"
                :items="deptTypeOptions"
                label="Khối"
                placeholder="Tất cả khối"
                dense
                outlined
                clearable
                hide-details
                background-color="white"
              ></v-autocomplete>
            </v-col>

            <!-- Lọc theo Phòng / Chi nhánh -->
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filterDept"
                :items="deptOptions"
                label="Phòng / Chi nhánh"
                placeholder="Tất cả phòng/chi nhánh"
                dense
                outlined
                clearable
                hide-details
                background-color="white"
              ></v-autocomplete>
            </v-col>

            <!-- Lọc theo Trạng thái tài khoản -->
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterUserStatus"
                :items="userStatusOptions"
                label="Trạng thái tài khoản"
                placeholder="Tất cả"
                dense
                outlined
                clearable
                hide-details
                background-color="white"
              ></v-select>
            </v-col>

            <!-- Các nút thao tác lọc -->
            <v-col cols="12" sm="12" md="2" class="d-flex align-center justify-end">
              <v-btn
                color="teal darken-4"
                dark
                depressed
                height="40"
                class="rounded-lg mr-2 font-weight-bold flex-grow-1"
                @click="applyFilter"
              >
                <v-icon left small>mdi-filter</v-icon>
                Lọc
              </v-btn>
              <v-btn
                color="grey lighten-1"
                text
                height="40"
                class="rounded-lg font-weight-medium"
                @click="resetFilters"
              >
                <v-icon left small>mdi-restore</v-icon>
                Đặt lại
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-sheet>

      <!-- Thanh thống kê số liệu tóm tắt (Stats Bar) -->
      <div class="px-6 py-3 grey lighten-5 d-flex align-center flex-wrap" style="gap: 12px; border-bottom: 1px solid #E0E0E0;">
        <div class="caption font-weight-medium grey--text text--darken-2">
          Tổng số tài khoản: <strong class="teal--text text--darken-4 font-weight-bold">{{ filteredUsers.length }}</strong>
        </div>
        <v-chip v-if="stats.activeUsers > 0" small color="green lighten-5" text-color="green darken-4" class="font-weight-medium">
          <v-icon left x-small>mdi-check-circle</v-icon>
          Tài khoản hoạt động: {{ stats.activeUsers }}
        </v-chip>
        <v-chip v-if="stats.lockedUsers > 0" small color="red lighten-5" text-color="red darken-4" class="font-weight-medium">
          <v-icon left x-small>mdi-lock</v-icon>
          Tài khoản bị khóa: {{ stats.lockedUsers }}
        </v-chip>
        <v-chip v-if="filterDeptType" small color="teal lighten-5" text-color="teal darken-4" class="font-weight-medium">
          <v-icon left x-small>mdi-domain</v-icon>
          Khối: {{ filterDeptType }}
        </v-chip>
        <v-spacer></v-spacer>
        <div class="caption grey--text d-none d-sm-inline-block">
          Cập nhật lần cuối: {{ lastUpdatedText }}
        </div>
      </div>

      <!-- Bảng dữ liệu danh sách tài khoản IT -->
      <v-data-table 
        class="my-custom-table"
        height="calc(100vh - 350px)"
        fixed-header
        :mobile-breakpoint="0"
        :items="filteredUsers"
        :headers="headers"
        :loading="isLoading"
        loading-text="Đang tải dữ liệu tài khoản..."
        no-data-text="Không tìm thấy tài khoản nào"
        :footer-props="{
          itemsPerPageOptions: [15, 50, 100, 200, 500],
          itemsPerPageText: 'Số dòng mỗi trang'
        }"
        @contextmenu:row.prevent="rowRightClickHandler"
      >
        <!-- Cột Nhân sự (Mã NS & Tên NS kết hợp avatar) -->
        <template v-slot:[`item.employeeName`]="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="32" color="teal lighten-5" class="mr-2 flex-shrink-0">
              <span class="teal--text text--darken-4 font-weight-bold caption">
                {{ getAvatarLetter(item.employeeName) }}
              </span>
            </v-avatar>
            <div class="d-flex flex-column overflow-hidden">
              <span class="font-weight-bold caption teal--text text--darken-4 text-truncate" :title="item.employeeName">
                {{ item.employeeName }}
              </span>
              <span class="caption grey--text text-truncate" style="font-size: 11px !important;">
                <v-icon x-small class="mr-0.5">mdi-card-account-details-outline</v-icon>
                {{ item.employeeCode }}
              </span>
            </div>
          </div>
        </template>

        <!-- Cột Mã nhân sự riêng nếu cần -->
        <template v-slot:[`item.employeeCode`]="{ item }">
          <span class="caption font-weight-bold blue-grey--text text--darken-3">{{ item.employeeCode }}</span>
        </template>

        <!-- Cột Khối & Phòng ban -->
        <template v-slot:[`item.departmentName`]="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="caption font-weight-bold blue-grey--text text--darken-4">
              {{ item.departmentName || '--' }}
            </span>
            <span v-if="item.departmentTypeName" class="caption grey--text text--darken-1" style="font-size: 11px !important;">
              <v-icon x-small class="mr-0.5">mdi-domain</v-icon>
              {{ item.departmentTypeName }}
            </span>
          </div>
        </template>

        <!-- Cột Chức danh -->
        <template v-slot:[`item.positionStateName`]="{ item }">
          <v-chip v-if="item.positionStateName" x-small label color="teal lighten-5" text-color="teal darken-4" class="font-weight-medium caption">
            {{ item.positionStateName }}
          </v-chip>
          <span v-else class="grey--text caption">--</span>
        </template>

        <!-- Cột Tên đăng nhập / Tài khoản -->
        <template v-slot:[`item.userName`]="{ item }">
          <code class="caption px-2 py-1 rounded grey lighten-4 teal--text text--darken-4 font-weight-bold">
            {{ item.userName }}
          </code>
        </template>

        <!-- Cột Trạng thái nhân viên -->
        <template v-slot:[`item.employeeStatus`]="{ item }">
          <v-chip
            small
            label
            :color="getEmpStatusBadge(item.employeeStatus).color"
            :text-color="getEmpStatusBadge(item.employeeStatus).textColor"
            class="font-weight-medium caption"
          >
            <v-icon left x-small>{{ getEmpStatusBadge(item.employeeStatus).icon }}</v-icon>
            {{ item.employeeStatus || 'Chưa cập nhật' }}
          </v-chip>
        </template>

        <!-- Cột Trạng thái tài khoản -->
        <template v-slot:[`item.userStatus`]="{ item }">
          <v-chip
            small
            label
            :color="getUserStatusBadge(item.userStatus).color"
            :text-color="getUserStatusBadge(item.userStatus).textColor"
            class="font-weight-medium caption"
          >
            <v-icon left x-small>{{ getUserStatusBadge(item.userStatus).icon }}</v-icon>
            {{ item.userStatus || 'Bình thường' }}
          </v-chip>
        </template>

        <!-- Cột Hành động (Hành động nhanh + Menu mở rộng gọn gàng) -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center flex-wrap" style="gap: 4px; min-width: 260px;">
            <!-- Nút Chuyển thiết bị -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="teal lighten-5"
                  class="teal--text text--darken-4 font-weight-bold"
                  v-bind="attrs"
                  v-on="on"
                  @click="openResetDevice(item)"
                >
                  <v-icon left x-small>mdi-cellphone-link</v-icon> Chuyển TB
                </v-btn>
              </template>
              <span>Chuyển / Reset thiết bị đăng nhập</span>
            </v-tooltip>

            <!-- Nút Cập nhật mật khẩu -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="orange lighten-5"
                  class="orange--text text--darken-4 font-weight-bold"
                  v-bind="attrs"
                  v-on="on"
                  @click="openChangePassword(item)"
                >
                  <v-icon left x-small>mdi-lock-reset</v-icon> Đổi MK
                </v-btn>
              </template>
              <span>Thay đổi mật khẩu tài khoản</span>
            </v-tooltip>

            <!-- Nút Xem lịch sử điều chuyển -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="indigo lighten-5"
                  class="indigo--text text--darken-3 font-weight-bold"
                  v-bind="attrs"
                  v-on="on"
                  @click="showRotationHistory(item)"
                >
                  <v-icon left x-small>mdi-history</v-icon> Lịch sử
                </v-btn>
              </template>
              <span>Xem lịch sử điều chuyển nhân viên</span>
            </v-tooltip>

            <!-- Menu hành động bổ sung -->
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="grey lighten-4"
                  class="grey--text text--darken-3 font-weight-bold px-1"
                  v-bind="attrs"
                  v-on="on"
                  :loading="loadingERPCode === (item.employeeCode || item.code)"
                >
                  <v-icon x-small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1">
                <v-list-item @click="setFaceAllow(item)">
                  <v-list-item-icon class="mr-2 my-1">
                    <v-icon small color="teal darken-3">mdi-face-recognition</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-medium">Đăng ký gương mặt</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addWebHandler(item)">
                  <v-list-item-icon class="mr-2 my-1">
                    <v-icon small color="blue darken-3">mdi-web</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-medium">Chấm công web</v-list-item-title>
                </v-list-item>
                <v-divider class="my-1"></v-divider>
                <!-- Thao tác đồng bộ dữ liệu ERP -->
                <v-list-item @click="postERP(item)">
                  <v-list-item-icon class="mr-2 my-1">
                    <v-icon small color="purple darken-2">mdi-cloud-upload</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-medium">Tạo trên ERP</v-list-item-title>
                </v-list-item>
                <v-list-item @click="putERP(item)">
                  <v-list-item-icon class="mr-2 my-1">
                    <v-icon small color="teal darken-2">mdi-cloud-sync</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-medium">Cập nhật lên ERP</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>

      <!-- Custom Dialog chuyển thiết bị -->
      <cfm-dialog :dialog="cfmDialog" :item="selectItem" @close-cfm-dialog="cfmDialog = false; selectItem = null"></cfm-dialog>

      <!-- Hộp thoại Thay đổi mật khẩu tài khoản -->
      <v-dialog v-model="changePasswordDialog" max-width="500px" persistent>
        <v-card class="rounded-xl">
          <!-- Tiêu đề Dialog đổi mật khẩu -->
          <v-card-title class="teal darken-4 white--text py-3 px-6 d-flex align-center">
            <v-icon left color="white">mdi-lock-reset</v-icon>
            <span class="text-subtitle-1 font-weight-bold">Thay đổi mật khẩu tài khoản</span>
            <v-spacer></v-spacer>
            <v-btn icon dark small @click="changePasswordDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-6">
            <!-- Thông tin tài khoản được chọn -->
            <div v-if="selectItem" class="mb-4 pa-3 grey lighten-4 rounded-lg d-flex align-center">
              <v-avatar size="36" color="teal lighten-5" class="mr-3">
                <v-icon color="teal darken-4" small>mdi-account</v-icon>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="font-weight-bold subtitle-2 teal--text text--darken-4">
                  {{ selectItem.employeeName }}
                </span>
                <span class="caption grey--text">
                  Tài khoản: <strong>{{ selectItem.userName }}</strong> ({{ selectItem.employeeCode }})
                </span>
              </div>
            </div>

            <!-- Form nhập mật khẩu mới -->
            <v-form ref="it_change_password" id="it_change_password" v-model="itChangePasswordValid" @submit.prevent="changePasswordHandler">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="changePasswordForm.password"
                    label="Mật khẩu mới *"
                    placeholder="Nhập mật khẩu mới"
                    outlined
                    dense
                    :type="showPass1 ? 'text' : 'password'"
                    :append-icon="showPass1 ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showPass1 = !showPass1"
                    :rules="[v => !!v || 'Vui lòng nhập mật khẩu mới', v => (v && v.length >= 6) || 'Tối thiểu 6 ký tự']"
                    background-color="white"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="changePasswordForm.confirmPassword"
                    label="Xác nhận mật khẩu mới *"
                    placeholder="Nhập lại mật khẩu mới"
                    outlined
                    dense
                    :type="showPass2 ? 'text' : 'password'"
                    :append-icon="showPass2 ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showPass2 = !showPass2"
                    :rules="[
                      v => !!v || 'Vui lòng xác nhận mật khẩu',
                      v => v === changePasswordForm.password || 'Mật khẩu xác nhận không khớp'
                    ]"
                    background-color="white"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Nút hành động của Dialog đổi mật khẩu -->
          <v-card-actions class="px-6 py-3">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              class="rounded-lg font-weight-medium px-4"
              @click="changePasswordDialog = false"
            >
              Hủy
            </v-btn>
            <v-btn
              type="submit"
              form="it_change_password"
              color="teal darken-4"
              dark
              depressed
              class="rounded-lg font-weight-bold px-5"
            >
              Cập nhật mật khẩu
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Hộp thoại Xem lịch sử điều chuyển nhân viên -->
      <v-dialog v-model="rotationDialog" max-width="1000px" scrollable>
        <v-card class="rounded-xl">
          <!-- Tiêu đề Dialog lịch sử điều chuyển -->
          <v-card-title class="teal darken-4 white--text py-3 px-6 d-flex align-center">
            <v-icon left color="white">mdi-history</v-icon>
            <span class="text-subtitle-1 font-weight-bold">
              Lịch sử điều chuyển: {{ selectItem ? selectItem.employeeName : '' }} ({{ selectItem ? selectItem.employeeCode : '' }})
            </span>
            <v-spacer></v-spacer>
            <v-btn icon dark small @click="rotationDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-4">
            <v-data-table
              :headers="rotationHeaders"
              :items="rotationHistory"
              :loading="rotationLoading"
              loading-text="Đang tải dữ liệu lịch sử điều chuyển..."
              no-data-text="Không có lịch sử điều chuyển cho nhân viên này"
              class="my-custom-table"
              dense
            >
              <!-- Format dates -->
              <template v-slot:[`item.rotationDate`]="{ item }">
                {{ formatDate(item.rotationDate) }}
              </template>
              <template v-slot:[`item.rotation_date`]="{ item }">
                {{ formatDate(item.rotation_date) }}
              </template>
              <template v-slot:[`item.effectDate`]="{ item }">
                {{ formatDate(item.effectDate) }}
              </template>
              <template v-slot:[`item.effect_date`]="{ item }">
                {{ formatDate(item.effect_date) }}
              </template>
              <template v-slot:[`item.applyDate`]="{ item }">
                {{ formatDate(item.applyDate) }}
              </template>
              <template v-slot:[`item.apply_date`]="{ item }">
                {{ formatDate(item.apply_date) }}
              </template>
              <template v-slot:[`item.date`]="{ item }">
                {{ formatDate(item.date) }}
              </template>
            </v-data-table>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="px-6 py-3">
            <v-spacer></v-spacer>
            <v-btn
              color="teal darken-4"
              dark
              depressed
              class="rounded-lg font-weight-bold px-5"
              @click="rotationDialog = false"
            >
              Đóng
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar thông báo nhanh kết quả hành động -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top right rounded="lg">
        <div class="d-flex align-center">
          <v-icon left small color="white">{{ snackbar.icon }}</v-icon>
          <span>{{ snackbar.text }}</span>
        </div>
      </v-snackbar>
    </v-card>
  </div>
</template>

<script>
// Nhập các component và dịch vụ
import UserService from '../../services/UserService';
import CfmDialog from './CfmDialog.vue';
import * as moment from 'moment/moment';

export default {
  name: 'UserList',
  components: {
    CfmDialog
  },

  data() {
    return {
      // Trạng thái tải dữ liệu
      isLoading: false,
      loadingERPCode: null,
      users: [],
      selectItem: null,

      // Thời gian cập nhật sau cùng
      lastUpdated: new Date(),

      // Các biến bộ lọc tìm kiếm
      search: '',
      filterDeptType: null,
      filterDept: null,
      filterUserStatus: null,

      // Danh sách tùy chọn trạng thái tài khoản
      userStatusOptions: [
        { text: 'Tất cả trạng thái', value: null },
        { text: 'Hoạt động', value: 'Hoạt động' },
        { text: 'Khóa / Bị chặn', value: 'Khóa' }
      ],

      // Cấu hình các cột cho bảng danh sách tài khoản IT
      headers: [
        { text: 'Nhân sự', value: 'employeeName', width: '220px', sortable: true },
        { text: 'Phòng / Chi nhánh', value: 'departmentName', width: '220px', sortable: true },
        { text: 'Chức danh', value: 'positionStateName', width: '150px', sortable: true },
        { text: 'Tài khoản', value: 'userName', width: '130px', sortable: true },
        { text: 'Trạng thái nhân viên', value: 'employeeStatus', width: '160px', sortable: true, align: 'center' },
        { text: 'Trạng thái tài khoản', value: 'userStatus', width: '160px', sortable: true, align: 'center' },
        { text: 'Hành động', value: 'actions', width: '280px', sortable: false, align: 'center' }
      ],

      // Hộp thoại xác nhận chuyển thiết bị
      cfmDialog: false,

      // Hộp thoại đổi mật khẩu
      changePasswordDialog: false,
      itChangePasswordValid: false,
      showPass1: false,
      showPass2: false,
      changePasswordForm: {
        password: '',
        confirmPassword: '',
        oldPassword: 'oldpass'
      },

      // Hộp thoại lịch sử điều chuyển
      rotationDialog: false,
      rotationLoading: false,
      rotationHistory: [],

      // Thông báo phản hồi thao tác
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        icon: 'mdi-check-circle'
      }
    };
  },

  watch: {
    changePasswordDialog(val) {
      if (!val) {
        this.selectItem = null;
        this.showPass1 = false;
        this.showPass2 = false;
        this.changePasswordForm = {
          password: '',
          confirmPassword: '',
          oldPassword: 'oldpass'
        };
        if (this.$refs.it_change_password) {
          this.$refs.it_change_password.resetValidation();
        }
      }
    }
  },

  computed: {
    // Chuỗi thời gian cập nhật lần cuối
    lastUpdatedText() {
      if (!this.lastUpdated) return '--';
      return moment(this.lastUpdated).format('HH:mm:ss DD/MM/YYYY');
    },

    // Danh sách các Khối có trong dữ liệu
    deptTypeOptions() {
      const set = new Set();
      (this.users || []).forEach(u => {
        if (u.departmentTypeName) set.add(u.departmentTypeName);
      });
      return Array.from(set).sort();
    },

    // Danh sách các Phòng ban / Chi nhánh
    deptOptions() {
      const set = new Set();
      (this.users || []).forEach(u => {
        if (u.departmentName) set.add(u.departmentName);
      });
      return Array.from(set).sort();
    },

    // Dữ liệu sau khi áp dụng bộ lọc và từ khóa tìm kiếm
    filteredUsers() {
      let result = this.users || [];

      // Lọc theo Khối
      if (this.filterDeptType) {
        result = result.filter(u => u.departmentTypeName === this.filterDeptType);
      }

      // Lọc theo Phòng ban
      if (this.filterDept) {
        result = result.filter(u => u.departmentName === this.filterDept);
      }

      // Lọc theo Trạng thái tài khoản
      if (this.filterUserStatus) {
        result = result.filter(u => {
          const status = String(u.userStatus || '').toLowerCase();
          if (this.filterUserStatus === 'Hoạt động') {
            return status.includes('hoạt động') || status.includes('active') || status.includes('ok');
          }
          return status.includes('khóa') || status.includes('lock') || status.includes('chặn');
        });
      }

      // Tìm kiếm từ khóa nhanh
      if (this.search && this.search.trim() !== '') {
        const keyword = this.search.trim().toLowerCase();
        result = result.filter(u => {
          const name = String(u.employeeName || '').toLowerCase();
          const code = String(u.employeeCode || '').toLowerCase();
          const username = String(u.userName || '').toLowerCase();
          const dept = String(u.departmentName || '').toLowerCase();
          const deptType = String(u.departmentTypeName || '').toLowerCase();
          const position = String(u.positionStateName || '').toLowerCase();

          return name.includes(keyword) ||
            code.includes(keyword) ||
            username.includes(keyword) ||
            dept.includes(keyword) ||
            deptType.includes(keyword) ||
            position.includes(keyword);
        });
      }

      return result;
    },

    // Thống kê nhanh số lượng tài khoản
    stats() {
      let activeUsers = 0;
      let lockedUsers = 0;

      (this.users || []).forEach(u => {
        const status = String(u.userStatus || '').toLowerCase();
        if (status.includes('khóa') || status.includes('lock') || status.includes('chặn')) {
          lockedUsers++;
        } else {
          activeUsers++;
        }
      });

      return {
        total: this.users.length,
        activeUsers,
        lockedUsers
      };
    },

    // Xây dựng tiêu đề cột động cho lịch sử điều chuyển
    rotationHeaders() {
      if (!this.rotationHistory || this.rotationHistory.length === 0) return [];
      
      const labelMap = {
        decisionNo: 'Số quyết định',
        decision_no: 'Số quyết định',
        soQuyetDinh: 'Số quyết định',
        so_quyet_dinh: 'Số quyết định',
        rotationDate: 'Ngày điều chuyển',
        rotation_date: 'Ngày điều chuyển',
        ngayDieuChuyen: 'Ngày điều chuyển',
        ngay_dieu_chuyen: 'Ngày điều chuyển',
        effectDate: 'Ngày hiệu lực',
        effect_date: 'Ngày hiệu lực',
        applyDate: 'Ngày áp dụng',
        apply_date: 'Ngày áp dụng',
        date: 'Ngày',
        oldDepartmentName: 'Phòng ban cũ',
        old_department_name: 'Phòng ban cũ',
        oldDeptName: 'Phòng ban cũ',
        old_dept_name: 'Phòng ban cũ',
        oldDepartment: 'Phòng ban cũ',
        fromDepartment: 'Phòng ban cũ',
        newDepartmentName: 'Phòng ban mới',
        new_department_name: 'Phòng ban mới',
        newDeptName: 'Phòng ban mới',
        new_dept_name: 'Phòng ban mới',
        newDepartment: 'Phòng ban mới',
        toDepartment: 'Phòng ban mới',
        oldPositionName: 'Chức vụ cũ',
        old_position_name: 'Chức vụ cũ',
        oldPosition: 'Chức vụ cũ',
        fromPosition: 'Chức vụ cũ',
        newPositionName: 'Chức vụ mới',
        new_position_name: 'Chức vụ mới',
        newPosition: 'Chức vụ mới',
        toPosition: 'Chức vụ mới',
        reason: 'Lý do',
        lyDo: 'Lý do',
        ly_do: 'Lý do',
        note: 'Ghi chú',
        ghiChu: 'Ghi chú',
        ghi_chu: 'Ghi chú'
      };

      const orderedKeys = [
        'decisionNo', 'decision_no', 'soQuyetDinh', 'so_quyet_dinh',
        'rotationDate', 'rotation_date', 'ngayDieuChuyen', 'ngay_dieu_chuyen', 'effectDate', 'effect_date', 'applyDate', 'apply_date', 'date',
        'oldDepartmentName', 'old_department_name', 'oldDeptName', 'old_dept_name', 'oldDepartment', 'fromDepartment',
        'newDepartmentName', 'new_department_name', 'newDeptName', 'new_dept_name', 'newDepartment', 'toDepartment',
        'oldPositionName', 'old_position_name', 'oldPosition', 'fromPosition',
        'newPositionName', 'new_position_name', 'newPosition', 'toPosition',
        'reason', 'lyDo', 'ly_do', 'note', 'ghiChu', 'ghi_chu'
      ];

      const presentKeys = new Set();
      this.rotationHistory.forEach(item => {
        Object.keys(item).forEach(k => {
          if (k !== 'id' && k !== 'employeeCode' && k !== 'employeecode' && k !== 'employee_code' && k !== 'employeeName' && k !== 'employeename' && k !== 'employee_name') {
            presentKeys.add(k);
          }
        });
      });

      const headers = [];
      orderedKeys.forEach(key => {
        if (presentKeys.has(key)) {
          headers.push({
            text: labelMap[key] || key,
            value: key
          });
          presentKeys.delete(key);
        }
      });

      presentKeys.forEach(key => {
        headers.push({
          text: labelMap[key] || key,
          value: key
        });
      });

      return headers;
    }
  },

  created() {
    this.userService = new UserService();
  },

  mounted() {
    this.loadUsers();
  },

  methods: {
    // Lấy chữ cái đầu tiên cho avatar
    getAvatarLetter(name) {
      if (!name) return 'U';
      const parts = name.trim().split(/\s+/);
      return parts[parts.length - 1].charAt(0).toUpperCase();
    },

    // Tải danh sách người dùng từ UserService
    loadUsers() {
      this.isLoading = true;
      return this.userService.gets()
        .then(d => {
          this.users = Array.isArray(d) ? d : (d?.data || []);
          this.lastUpdated = new Date();
        })
        .catch(err => {
          console.error('Lỗi khi tải danh sách người dùng:', err);
          this.showSnackbar('Không thể tải danh sách tài khoản', 'error', 'mdi-alert-circle');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Áp dụng bộ lọc
    applyFilter() {
      // computed filteredUsers tự động lọc
    },

    // Đặt lại các bộ lọc
    resetFilters() {
      this.search = '';
      this.filterDeptType = null;
      this.filterDept = null;
      this.filterUserStatus = null;
    },

    // Mở dialog chuyển thiết bị
    openResetDevice(item) {
      this.selectItem = item;
      this.cfmDialog = true;
    },

    // Mở dialog thay đổi mật khẩu
    openChangePassword(item) {
      this.selectItem = item;
      this.changePasswordDialog = true;
    },

    // Đăng ký gương mặt hợp lệ
    setFaceAllow(item) {
      return this.userService.setFaceValid(item.userCode)
        .then(() => {
          this.showSnackbar(`Đã cho phép đăng ký gương mặt cho ${item.employeeName}`, 'teal darken-4', 'mdi-check-circle');
        })
        .catch(err => {
          console.error(err);
          this.showSnackbar('Lỗi khi thao tác đăng ký gương mặt', 'error', 'mdi-alert-circle');
        });
    },

    // Thêm quyền chấm công web
    addWebHandler(item) {
      return this.userService.addWeb(item.userCode)
        .then(() => {
          this.showSnackbar(`Đã cấu hình chấm công web cho ${item.employeeName}`, 'teal darken-4', 'mdi-check-circle');
        })
        .catch(err => {
          console.error(err);
          this.showSnackbar('Lỗi khi thêm chấm công web', 'error', 'mdi-alert-circle');
        });
    },

    // Tạo thông tin nhân viên trên hệ thống ERP
    postERP(item) {
      const code = item.employeeCode || item.code;
      if (!code) {
        this.showSnackbar('Không tìm thấy mã nhân viên để tạo trên ERP', 'warning', 'mdi-alert');
        return;
      }
      this.loadingERPCode = code;
      return this.userService.postERP(code)
        .then((res) => {
          const msg = res?.data?.message || res?.data || `Tạo nhân viên ${item.employeeName || code} trên ERP thành công`;
          this.showSnackbar(typeof msg === 'string' ? msg : `Tạo nhân viên ${item.employeeName || code} trên ERP thành công`, 'teal darken-4', 'mdi-cloud-check');
        })
        .catch(err => {
          console.error(err);
          const errMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Lỗi khi tạo nhân viên trên ERP';
          this.showSnackbar(typeof errMsg === 'string' ? errMsg : 'Lỗi khi tạo nhân viên trên ERP', 'error', 'mdi-alert-circle');
        })
        .finally(() => {
          this.loadingERPCode = null;
        });
    },

    // Cập nhật thông tin nhân viên lên hệ thống ERP
    putERP(item) {
      const code = item.employeeCode || item.code;
      if (!code) {
        this.showSnackbar('Không tìm thấy mã nhân viên để cập nhật lên ERP', 'warning', 'mdi-alert');
        return;
      }
      this.loadingERPCode = code;
      return this.userService.putERP(code)
        .then((res) => {
          const msg = res?.data?.message || res?.data || `Cập nhật nhân viên ${item.employeeName || code} lên ERP thành công`;
          this.showSnackbar(typeof msg === 'string' ? msg : `Cập nhật nhân viên ${item.employeeName || code} lên ERP thành công`, 'teal darken-4', 'mdi-cloud-check');
        })
        .catch(err => {
          console.error(err);
          const errMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Lỗi khi cập nhật nhân viên lên ERP';
          this.showSnackbar(typeof errMsg === 'string' ? errMsg : 'Lỗi khi cập nhật nhân viên lên ERP', 'error', 'mdi-alert-circle');
        })
        .finally(() => {
          this.loadingERPCode = null;
        });
    },

    // Xử lý gửi form cập nhật mật khẩu
    changePasswordHandler() {
      if (this.$refs.it_change_password) {
        this.$refs.it_change_password.validate();
      }
      if (this.itChangePasswordValid && this.selectItem) {
        return this.userService.changePassword(this.selectItem.userCode, this.changePasswordForm)
          .then(() => {
            this.showSnackbar('Cập nhật mật khẩu thành công', 'teal darken-4', 'mdi-check-circle');
            this.itChangePasswordValid = false;
            this.changePasswordDialog = false;
          })
          .catch(err => {
            console.error(err);
            this.showSnackbar('Lỗi khi cập nhật mật khẩu', 'error', 'mdi-alert-circle');
          });
      }
    },

    // Hiển thị lịch sử điều chuyển
    showRotationHistory(item) {
      this.selectItem = item;
      this.rotationLoading = true;
      this.rotationHistory = [];
      this.rotationDialog = true;
      
      this.userService.getRotationDetails(item.employeeCode)
        .then(d => {
          if (Array.isArray(d)) {
            this.rotationHistory = d;
          } else if (d && Array.isArray(d.data)) {
            this.rotationHistory = d.data;
          } else {
            this.rotationHistory = [];
          }
        })
        .catch(err => {
          console.error(err);
          this.rotationHistory = [];
        })
        .finally(() => {
          this.rotationLoading = false;
        });
    },

    // Xử lý nhấp chuột phải vào dòng
    rowRightClickHandler(e, select) {
      this.selectItem = select.item;
    },

    // Định dạng ngày tháng
    formatDate(value) {
      if (!value) return '';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : value;
    },

    // Badge trạng thái nhân viên
    getEmpStatusBadge(status) {
      if (!status) return { color: 'grey lighten-4', textColor: 'grey darken-3', icon: 'mdi-circle-small' };
      const s = String(status).toLowerCase();
      if (s.includes('làm việc') || s.includes('hoạt động') || s.includes('active')) {
        return { color: 'green lighten-5', textColor: 'green darken-4', icon: 'mdi-check-circle' };
      }
      if (s.includes('nghỉ') || s.includes('quit') || s.includes('thôi việc')) {
        return { color: 'red lighten-5', textColor: 'red darken-4', icon: 'mdi-close-circle' };
      }
      if (s.includes('thử việc') || s.includes('probation')) {
        return { color: 'orange lighten-5', textColor: 'orange darken-4', icon: 'mdi-clock-outline' };
      }
      return { color: 'blue-grey lighten-5', textColor: 'blue-grey darken-4', icon: 'mdi-account' };
    },

    // Badge trạng thái tài khoản
    getUserStatusBadge(status) {
      if (!status) return { color: 'green lighten-5', textColor: 'green darken-4', icon: 'mdi-check-circle' };
      const s = String(status).toLowerCase();
      if (s.includes('khóa') || s.includes('lock') || s.includes('chặn') || s.includes('inactive')) {
        return { color: 'red lighten-5', textColor: 'red darken-4', icon: 'mdi-lock' };
      }
      return { color: 'green lighten-5', textColor: 'green darken-4', icon: 'mdi-check-circle' };
    },

    // Hiển thị snackbar thông báo
    showSnackbar(text, color = 'teal darken-4', icon = 'mdi-check-circle') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.icon = icon;
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
/* Tùy chỉnh phong cách chuẩn cho v-data-table */
.my-custom-table >>> table {
  border-collapse: collapse;
}

.my-custom-table >>> th {
  background-color: #F8F9FA !important;
  color: #263238 !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #E0E0E0 !important;
  height: 44px !important;
  white-space: nowrap;
}

.my-custom-table >>> td {
  font-size: 13px !important;
  border-bottom: 1px solid #ECEFF1 !important;
  height: 48px !important;
}

.my-custom-table >>> tbody tr:hover {
  background-color: #F1F8E9 !important;
  transition: background-color 0.15s ease-in-out;
}
</style>